// Leads By Source (donut) — 8 tokens, cycled + varied beyond that.
export const SOURCE_PALETTE = [
  '#FF98B0',
  '#CB96CB',
  '#9EC6FF',
  '#F9E08B',
  '#D196FF',
  '#F38A3F',
  '#50C796',
  '#BFBFBF'
];

// Lead Count & Revenue By Status — 10 tokens, cycled + varied beyond that.
export const STATUS_PALETTE = [
  '#FF98B0',
  '#BAD7FF',
  '#96CBCB',
  '#F9E08B',
  '#F59E60',
  '#ABEBAB',
  '#AAEAFF',
  '#D196FF',
  '#30B474',
  '#F88980'
];

export const LOCATION_BAR_COLOR = '#05A557';
export const REVENUE_LINE_COLOR = '#30B474';
export const CHART_TOOLTIP_BG_COLOR = '#1F1F1F';
export const CHART_TOOLTIP_TEXT_COLOR = '#FFFFFF';

export const ANALYTICS_TOOLTIP_STYLE = {
  backgroundColor: CHART_TOOLTIP_BG_COLOR,
  borderColor: CHART_TOOLTIP_BG_COLOR,
  borderRadius: 8,
  padding: [6, 12],
  confine: true,
  extraCssText:
    'max-width:280px;white-space:normal;overflow-wrap:break-word;word-break:break-word;',
  textStyle: {
    color: CHART_TOOLTIP_TEXT_COLOR,
    fontSize: 13,
    fontWeight: 500
  }
};

interface Hsl {
  hue: number;
  saturation: number;
  lightness: number;
}

// Helper function to parse the hex color and get the channel value
const parseHexChannel = (hex: string, offset: number) =>
  parseInt(hex.slice(offset, offset + 2), 16) / 255;

// Helper function to convert hex color to HSL
const hexToHsl = (hex: string): Hsl => {
  const raw = hex.replace('#', '');
  const red = parseHexChannel(raw, 0);
  const green = parseHexChannel(raw, 2);
  const blue = parseHexChannel(raw, 4);

  const channelMax = Math.max(red, green, blue);
  const channelMin = Math.min(red, green, blue);
  const delta = channelMax - channelMin;
  const lightness = (channelMax + channelMin) / 2;

  if (delta === 0) {
    return { hue: 0, saturation: 0, lightness: lightness * 100 };
  }

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));

  let rawHue = 0;
  if (channelMax === red) rawHue = ((green - blue) / delta) % 6;
  else if (channelMax === green) rawHue = (blue - red) / delta + 2;
  else rawHue = (red - green) / delta + 4;

  return {
    hue: (rawHue * 60 + 360) % 360,
    saturation: saturation * 100,
    lightness: lightness * 100
  };
};

// Helper function to convert HSL to hex color
const hslToHex = ({ hue, saturation, lightness }: Hsl): string => {
  const sat = saturation / 100;
  const lgt = lightness / 100;
  const chroma = (1 - Math.abs(2 * lgt - 1)) * sat;
  const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const base = lgt - chroma / 2;

  // Each 60° sector maps chroma & secondary to RGB differently
  let red = 0;
  let green = 0;
  let blue = 0;
  if (hue < 60) {
    red = chroma;
    green = secondary;
  } else if (hue < 120) {
    red = secondary;
    green = chroma;
  } else if (hue < 180) {
    green = chroma;
    blue = secondary;
  } else if (hue < 240) {
    green = secondary;
    blue = chroma;
  } else if (hue < 300) {
    red = secondary;
    blue = chroma;
  } else {
    red = chroma;
    blue = secondary;
  }

  const toHex = (channel: number) =>
    Math.round((channel + base) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const OVERFLOW_HUE_STEP = 47;
const OVERFLOW_LIGHTNESS_NUDGE = 6;
const MIN_LIGHTNESS = 22;
const MAX_LIGHTNESS = 90;

// Helper function to get the color for the item at the given index
export const getPaletteColor = (palette: string[], index: number): string => {
  if (index < palette.length) return palette[index];

  const baseColor = palette[index % palette.length];
  const overflowCycle = Math.floor(index / palette.length);
  const { hue, saturation, lightness } = hexToHsl(baseColor);

  const rotatedHue = (hue + overflowCycle * OVERFLOW_HUE_STEP) % 360;
  const nudgedLightness = clamp(
    lightness +
      (overflowCycle % 2 === 0
        ? -OVERFLOW_LIGHTNESS_NUDGE
        : OVERFLOW_LIGHTNESS_NUDGE),
    MIN_LIGHTNESS,
    MAX_LIGHTNESS
  );

  return hslToHex({
    hue: rotatedHue,
    saturation,
    lightness: nudgedLightness
  });
};

// Deterministic index into a palette from a string key. A given source name
// always maps to the same color regardless of its position in the API payload,
// so colors stay stable across reloads (unlike raw array-index assignment).
export const getStableIndex = (key: string, size: number): number => {
  if (size <= 0) return 0;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 2147483647;
  }
  return hash % size;
};
